import Input from "../inputs/Input";
import { AiOutlineLoading } from "react-icons/ai";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
  Tabs,
  Timeline,
  TimelineBody,
  TimelineContent,
  TimelineItem,
  TimelinePoint,
  TimelineTime,
  TimelineTitle,
} from "flowbite-react";
import useCEP from "../../hooks/useCEP";
import { useForm } from "react-hook-form";
import { ADMIN, PAYMENTS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useRef, useState } from "react";
import CPFInput from "../inputs/CPFInput";
import PhoneInput from "../inputs/PhoneInput";
import { HiCalendar, HiInformationCircle } from "react-icons/hi";
import SelectInput from "../inputs/SelectInput";
import { formatToReal } from "../../utils/utils";
import CurrencyInput from "../inputs/CurrencyInput";
import CNPJInput from "../inputs/CNPJInput";
import TextAreaInput from "../inputs/TextAreaInput";
import DrawerLoading from "../DrawerLoading";
import { useQuery } from "react-query";
import moment from "moment";
import { FaTrash, FaUser } from "react-icons/fa6";
import { MdLocalPrintshop } from "react-icons/md";
import { useReactToPrint } from "react-to-print";

const CompanyEditForm = ({
  company,
  fetchCompanies,
  isDrawerOpen,
  onClose,
  categories,
}) => {
  const tabsRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingAddCredit, setIsSubmittingAddCredit] = useState(false);
  const [isSubmittingAnnotation, setIsSubmittingAnnotation] = useState(false);
  const [, setKey] = useState(0);
  const [error, setError] = useState(null);
  const [errorAnnotation, setErrorAnnotation] = useState(null);
  const reportRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: reportRef });

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (company) {
      Object.keys(company).forEach((key) => {
        setValue(key, company[key]);
      });
      setKey((prev) => prev + 1);
    }

    setError(null);
  }, [company, isDrawerOpen]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      if (data.vcm === "") {
        delete data.vcm;
      }

      await axios.put(`${ADMIN.GET_COMPANY}/${company?.id}`, data);

      onClose(false);

      fetchCompanies();

      setError(null);
    } catch (error) {
      setError(error);
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitAddCredit = async (data) => {
    try {
      setIsSubmittingAddCredit(true);

      if (+data?.value <= 0) {
        throw new Error("Valor inválido!");
      }

      let bodyData = {
        payment: {
          customer: company.id,
          value: data.value,
          methodPayment: data.methodPayment,
        },
      };

      await axios.post(`${PAYMENTS.ADD_CREDIT_MANUALLY}`, bodyData);

      onClose(false);

      fetchCompanies();

      setError(null);
    } catch (error) {
      setError(error);
      console.error(error);
    } finally {
      setIsSubmittingAddCredit(false);
    }
  };

  const {
    cep,
    setCep,
    address,
    loading: loadingCEP,
    error: errorCEP,
  } = useCEP();

  const onClickSearchCEP = (e) => {
    setCep(getValues("addressPostalCode"));
  };

  useEffect(() => {
    setValue("address", address?.logradouro);
    setValue("addressNeighborhood", address?.bairro);
    setValue("addressCity", address?.localidade);
  }, [address]);

  return (
    <div className="relative">
      <Tabs
        aria-label="Tabs with underline"
        variant="underline"
        ref={tabsRef}
        onActiveTabChange={(tab) => setActiveTab(tab)}
      >
        <Tabs.Item active title="Informações">
          <form className="mb-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <SelectInput
                label="Status do cliente *"
                name="status"
                required={true}
                register={register}
              >
                <option value={1}>Ativo</option>
                <option value={0}>Bloqueado</option>
                <option value={-1}>Inadimplente</option>
              </SelectInput>
            </div>
            <div className="mb-4">
              <SelectInput
                label="Tipo de cliente *"
                name="typeCustomer"
                required={true}
                register={register}
              >
                <option value="CPF">Pessoa física</option>
                <option value="CNPJ">Pessoa jurídica</option>
              </SelectInput>
            </div>
            {watch("typeCustomer") === "CPF" ? (
              <>
                <div className="mb-4">
                  <Input
                    type="text"
                    label="Nome *"
                    name="name"
                    required={true}
                    register={register}
                    error={errors?.name && "Este campo é obrigatório"}
                    value={watch("name")}
                  />
                </div>
                <div className="mb-4">
                  <CPFInput
                    type="text"
                    label="CPF *"
                    name="document"
                    required={true}
                    register={register}
                    error={errors?.document && "Este campo é obrigatório"}
                    value={watch("document") || ""}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <Input
                    type="text"
                    label="Razão Social"
                    name="socialReason"
                    required={false}
                    register={register}
                    value={watch("socialReason")}
                  />
                </div>

                <div className="mb-4">
                  <CNPJInput
                    type="text"
                    label="CNPJ *"
                    name="document"
                    required={true}
                    register={register}
                    error={errors?.document && "Este campo é obrigatório"}
                    value={watch("document") || ""}
                  />
                </div>

                <div className="mb-4">
                  <Input
                    type="text"
                    label="Nome do responsável"
                    name="responsibleName"
                    required={false}
                    register={register}
                  />
                </div>

                <div className="mb-4">
                  <CPFInput
                    type="text"
                    label="CPF do responsável"
                    name="responsibleCPF"
                    required={false}
                    register={register}
                    error={errors?.cpf && "Este campo é obrigatório"}
                    value={watch("responsibleCPF") || ""}
                  />
                </div>

                <div className="mb-4">
                  <SelectInput
                    register={register}
                    label="Cargo"
                    name="position"
                  >
                    <option value="SOCIO">Sócio/administrador</option>
                    <option value="GERENTE">Gerente</option>
                    <option value="ADM">Administrador</option>
                  </SelectInput>
                </div>
              </>
            )}

            <div className="mb-4">
              <SelectInput
                register={register}
                label="Tipo de pagamento"
                name="typePayment"
              >
                <option value="PRE">Pré-Pago</option>
                <option value="POS">Pós-pago</option>
              </SelectInput>
            </div>
            {watch("typePayment") === "POS" && (
              <>
                <div className="mb-4">
                  <CurrencyInput
                    label="Valor limite de uso"
                    name="limitUse"
                    required={false}
                    register={register}
                    control={control}
                  />
                </div>
                <div className="mb-4">
                  <CurrencyInput
                    control={control}
                    label="Valor mínimo mensal"
                    name="vcm"
                    required={false}
                    register={register}
                  />
                </div>
                <div className="mb-4">
                  <SelectInput
                    register={register}
                    label="Dia de criação do boleto"
                    name="paymentCreationDay"
                  >
                    {Array.from({ length: 30 }).map((_, index) => (
                      <option key={index} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div className="mb-4">
                  <SelectInput
                    register={register}
                    label="Dia de vencimento do boleto"
                    name="paymentExpirationDay"
                  >
                    {Array.from({ length: 30 }).map((_, index) => (
                      <option key={index} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div className="flex items-center mb-4">
                  <input
                    id={`checkbox-show-logo`}
                    type="checkbox"
                    name={`generateAutomaticPayment`}
                    {...register(`generateAutomaticPayment`)}
                    className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor={`checkbox-show-logo`}
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Gerar boleto automaticamente?
                  </label>
                </div>
              </>
            )}

            {watch("typePayment") === "PRE" && (
              <div className="mb-4">
                <CurrencyInput
                  label="Valor mínimo de recarga"
                  name="minRechargePRE"
                  required={false}
                  register={register}
                  control={control}
                />
              </div>
            )}

            <div className="flex items-center mb-4">
              <input
                id={`checkbox-show-logo`}
                type="checkbox"
                name={`showLogo`}
                {...register(`showLogo`)}
                className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor={`checkbox-show-logo`}
                className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                Mostrar logo nas consultas?
              </label>
            </div>

            <div className="mb-4">
              <PhoneInput
                type="text"
                label="Telefone *"
                name="phoneNumber"
                required={true}
                register={register}
                error={errors?.phoneNumber && "Este campo é obrigatório"}
              />
            </div>

            <div className="mb-4">
              <Input
                type="email"
                label="E-mail"
                name="email"
                required={false}
                register={register}
              />
            </div>
            <div className="mb-4">
              <Input
                type="text"
                label="ID do Asaas"
                name="asaas_id"
                required={false}
                register={register}
              />
            </div>
            <div className="mb-4 flex gap-2">
              <Input
                type="text"
                label="CEP"
                name="cep"
                required={false}
                register={register}
                className="flex-1"
              />
              <Button
                onClick={onClickSearchCEP}
                className="flex align-center whitespace-nowrap secondary"
                style={{ width: "220px", paddingTop: "4px" }}
                isProcessing={loadingCEP}
                processingSpinner={
                  <AiOutlineLoading className="h-6 w-6 animate-spin" />
                }
              >
                Buscar CEP
              </Button>
            </div>
            <div className="mb-4">
              <Input
                type="text"
                label="Endereço"
                name="address"
                required={false}
                register={register}
              />
            </div>
            <div className="mb-4">
              <div className="flex gap-4">
                <Input
                  type="text"
                  label="Número"
                  name="addressNumber"
                  required={false}
                  register={register}
                  className="flex-1"
                />
                <Input
                  type="text"
                  label="Bairro"
                  name="addressNeighborhood"
                  required={false}
                  register={register}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="mb-4">
              <div className="flex gap-4">
                <Input
                  type="text"
                  label="Cidade"
                  name="addressCity"
                  required={false}
                  register={register}
                  className="flex-1"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mb-48 md:mb-4 primary"
              isProcessing={isSubmitting}
              processingSpinner={
                <AiOutlineLoading className="h-6 w-6 animate-spin" />
              }
              disabled={isSubmitting}
            >
              Atualizar cliente
            </Button>
            {error && (
              <Alert color="failure" icon={HiInformationCircle}>
                Erro ao atualizar cliente
              </Alert>
            )}
            <div className="mb-40 lg:mb-4"></div>
          </form>
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

export default CompanyEditForm;
